import { KeyBinding } from '../../../../shared/CharacterTypes';
import { DEFAULT_KEY_BINDINGS } from '../../../../config/input/InputBindings';
import { MAX_BARS, SLOTS_PER_PAGE } from '../../../../shared/hotbar/barLayout';

export type ActionCategory = 'movement' | 'combat' | 'ui' | 'targeting' | 'misc' | 'event';
export type ActionFilterCategory = ActionCategory | 'bars' | 'all';

export interface ActionBindingRow {
  action: string;
  displayName: string;
  category: ActionCategory;
  barId?: number;
  description?: string;
  primary: KeyBinding | null;
  secondary: KeyBinding | null;
  extraBindings: KeyBinding[];
}

export interface ActionMeta {
  label: string;
  category: ActionCategory;
  barId?: number;
  slotIndex?: number;
  shortSlotLabel?: string;
  description?: string;
}

const ORDINAL_NAMES = [
  'First', 'Second', 'Third', 'Fourth', 'Fifth',
  'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth',
];

function buildBarActionMetadata(): Record<string, ActionMeta> {
  const entries: Record<string, ActionMeta> = {};
  for (let bar = 1; bar <= MAX_BARS; bar++) {
    const barDesc = bar === 1 ? 'the main bar' : `action bar ${bar}`;
    for (let slot = 1; slot <= SLOTS_PER_PAGE; slot++) {
      entries[`bar${bar}_slot${slot}`] = {
        label: `Bar ${bar} · Slot ${slot}`,
        category: 'combat',
        barId: bar,
        slotIndex: slot,
        shortSlotLabel: `B${bar}·S${slot}`,
        description: `${ORDINAL_NAMES[slot - 1] ?? `Slot ${slot}`} slot on ${barDesc}`,
      };
    }
    entries[`bar${bar}_page_next`] = {
      label: `Bar ${bar} · Next Page`,
      category: 'combat',
      barId: bar,
      shortSlotLabel: `B${bar}·Pg+`,
      description: `Flip ${barDesc} to its next page`,
    };
    entries[`bar${bar}_page_prev`] = {
      label: `Bar ${bar} · Previous Page`,
      category: 'combat',
      barId: bar,
      shortSlotLabel: `B${bar}·Pg-`,
      description: `Flip ${barDesc} to its previous page`,
    };
  }
  return entries;
}

/** Human-readable display metadata for standard actions */
export const ACTION_METADATA: Record<string, ActionMeta> = {
  // Movement
  move_left: { label: 'Move Left', category: 'movement', description: 'Walk left horizontally' },
  move_right: { label: 'Move Right', category: 'movement', description: 'Walk right horizontally' },
  move_up: { label: 'Move Up', category: 'movement', description: 'Climb ladders and ropes' },
  move_down: { label: 'Move Down', category: 'movement', description: 'Duck or climb down ladders' },
  jump: { label: 'Jump', category: 'movement', description: 'Jump upward or ascend ropes' },
  autowalk: { label: 'Toggle Auto-Run', category: 'movement', description: 'Automatically move forward until interrupted' },

  // Combat
  basic_attack: { label: 'Basic Attack', category: 'combat', description: 'Primary weapon attack' },
  secondary_attack: { label: 'Secondary Attack', category: 'combat', description: 'Off-hand / secondary skill' },
  free_aim: { label: 'Free Aim (Hold)', category: 'combat', description: 'Hold to aim trajectory with mouse' },

  // Action Bars 1..6
  ...buildBarActionMetadata(),

  // UI
  inventory: { label: 'Inventory / Bags', category: 'ui', description: 'Open your backpack and items' },
  character_sheet: { label: 'Character Sheet', category: 'ui', description: 'View attributes and equipment' },
  talents: { label: 'Talent Tree', category: 'ui', description: 'Spend talent points' },
  skills: { label: 'Spellbook & Skills', category: 'ui', description: 'Browse all learned abilities' },
  quest_log: { label: 'Quest Log', category: 'ui', description: 'View active and completed quests' },
  world_map: { label: 'World Map', category: 'ui', description: 'Open the connected world map' },
  party: { label: 'Party Menu', category: 'ui', description: 'Manage group and invitations' },
  leaderboard: { label: 'Leaderboard', category: 'ui', description: 'View speedrun and player rankings' },
  trade: { label: 'Trade', category: 'ui', description: 'Trade with targeted player' },
  bank: { label: 'Bank Vault', category: 'ui', description: 'Open personal bank storage' },
  crafting: { label: 'Crafting', category: 'ui', description: 'Open crafting workstation' },
  key_bindings: { label: 'Key Bindings', category: 'ui', description: 'Open the Key Bindings window' },

  // Targeting & Misc
  interact: { label: 'Interact / Talk', category: 'misc', description: 'Speak with NPCs or use objects' },
  loot: { label: 'Loot', category: 'misc', description: 'Pick up nearby item drops' },
  sit: { label: 'Sit / Rest', category: 'misc', description: 'Sit down to recover HP/MP' },
  target_next: { label: 'Target Next Enemy', category: 'targeting', description: 'Cycle through nearby targets' },
  target_prev: { label: 'Target Prev Enemy', category: 'targeting', description: 'Cycle backward through targets' },
  target_nearest: { label: 'Target Nearest Enemy', category: 'targeting', description: 'Select the closest enemy' },
  clear_target: { label: 'Clear Target', category: 'targeting', description: 'Deselect current target' },
  toggle_autoattack: { label: 'Toggle Auto-Attack', category: 'targeting', description: 'Engage continuous combat' },
  fishing_cast: { label: 'Fishing Cast', category: 'event', description: 'Cast fishing line' },
  fishing_reel: { label: 'Fishing Reel', category: 'event', description: 'Reel in active catch' },
};

/** Category labels and ordering */
export const CATEGORY_ORDER: Array<ActionCategory | 'bars'> = [
  'bars',
  'movement',
  'combat',
  'ui',
  'targeting',
  'misc',
  'event',
];

export const CATEGORY_LABELS: Record<ActionCategory | 'bars', string> = {
  bars: 'Bars',
  movement: 'Movement',
  combat: 'Combat & Abilities',
  ui: 'UI & Menus',
  targeting: 'Targeting',
  misc: 'Interaction',
  event: 'Events',
};

/**
 * Group flat KeyBinding array into deduplicated ActionBindingRow list.
 * Preserves stable action ordering matching canonical defaults, and includes
 * per-bar rows only for bars the character currently has (`activeBarIds`,
 * defaulting to `[1]`) or bars with an explicit bound key.
 */
export function groupKeybindingsByAction(
  bindings: readonly KeyBinding[],
  activeBarIds: readonly number[] = [1],
): ActionBindingRow[] {
  const activeBarSet = new Set<number>(activeBarIds.length > 0 ? activeBarIds : [1]);
  activeBarSet.add(1);

  const actionMap = new Map<string, KeyBinding[]>();
  for (const b of bindings) {
    const list = actionMap.get(b.action) ?? [];
    list.push(b);
    actionMap.set(b.action, list);
    const meta = ACTION_METADATA[b.action];
    if (meta?.barId) activeBarSet.add(meta.barId);
  }

  const orderedActions: string[] = [];
  for (const def of DEFAULT_KEY_BINDINGS.bindings) {
    const meta = ACTION_METADATA[def.action];
    if (meta?.barId && !activeBarSet.has(meta.barId)) continue;
    if (!orderedActions.includes(def.action)) {
      orderedActions.push(def.action);
    }
  }

  for (const action of [...Object.keys(ACTION_METADATA), ...actionMap.keys()]) {
    const meta = ACTION_METADATA[action];
    if (meta?.barId && !activeBarSet.has(meta.barId)) continue;
    if (!orderedActions.includes(action)) {
      orderedActions.push(action);
    }
  }

  return orderedActions.map((action) => {
    const list = actionMap.get(action) ?? [];
    const meta = ACTION_METADATA[action];
    const category: ActionCategory = meta?.category
      ?? (list[0]?.category as ActionCategory)
      ?? 'misc';

    const displayName = meta?.label ?? action.replace(/_/g, ' ');
    const primary = list[0] ?? null;
    const secondary = list[1] ?? null;
    const extraBindings = list.slice(2);

    return {
      action,
      displayName,
      category,
      ...(meta?.barId !== undefined ? { barId: meta.barId } : {}),
      description: meta?.description,
      primary,
      secondary,
      extraBindings,
    };
  });
}
